<template>
  <!-- The variant colour sits on the wrapper; the black body leaves 2px of it
       exposed along the bottom, and that strip is the whole status treatment. -->
  <div class="gg-toast" :class="`gg-toast--${variant}`" role="alert">
    <div class="gg-toast__body">
      <span class="gg-toast__icon" aria-hidden="true">
        <v-icon size="20">{{ icon }}</v-icon>
      </span>
      <span class="gg-toast__text">{{ text }}</span>
      <button
        v-if="actionLabel"
        type="button"
        class="gg-toast__btn"
        @click="$emit('action')"
      >
        {{ actionLabel }}
      </button>
    </div>
  </div>
</template>

<script>
// The chip behind the glyph is a pill filled with the variant colour, and the glyph is
// punched out of it in black — so each icon has to be a FILLED, round-ish shape or the
// chip reads as a coloured dot with a mark floating in it. That is why warning is
// `alert-circle` and not `alert`, whose triangle leaves the pill showing at the corners.
const VARIANT_ICONS = {
  success: "mdi-check-circle",
  warning: "mdi-alert-circle",
  error: "mdi-close-octagon",
};

export default {
  name: "GloboGraphicsToast",
  props: {
    variant: {
      type: String,
      default: "error",
      validator: (v) => Object.keys(VARIANT_ICONS).includes(v),
    },
    text: { type: String, required: true },
    // Omit for a message-only toast.
    actionLabel: { type: String, default: "" },
  },
  computed: {
    // Was a success/else ternary; a third variant makes that a lookup. Falls back to
    // the error glyph for the same reason the `variant` prop defaults to error — an
    // unknown state is not a good one.
    icon() {
      return VARIANT_ICONS[this.variant] || VARIANT_ICONS.error;
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-toast {
  position: fixed;
  right: var(--sc-space-6);
  bottom: var(--sc-space-6);
  z-index: 20;
  // Sizes to its content, capped so the long offline message wraps to two lines.
  width: max-content;
  max-width: min(624px, calc(100% - var(--sc-space-8)));
  padding-bottom: 2px;
  border-radius: var(--sc-radius-md);
}

.gg-toast--error {
  background: var(--sc-gg-error);
}

.gg-toast--warning {
  background: var(--sc-gg-warning);
}

.gg-toast--success {
  background: var(--sc-gg-success-strong);
}

.gg-toast__body {
  display: flex;
  align-items: center;
  gap: var(--sc-space-2);
  min-height: 50px;
  padding: 14px var(--sc-space-4);
  border-radius: var(--sc-radius-md);
  background: #000000;
}

.gg-toast__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: var(--sc-radius-pill);

  .v-icon {
    color: #000000 !important;
  }
}

.gg-toast--error .gg-toast__icon {
  padding: 1px;
  background: var(--sc-gg-error);
}

.gg-toast--warning .gg-toast__icon {
  background: var(--sc-gg-warning);
}

.gg-toast--success .gg-toast__icon {
  background: var(--sc-gg-success-strong);
}

.gg-toast__text {
  flex: 1 1 auto;
  font-size: 16px;
  font-weight: var(--sc-weight-medium);
  line-height: 20px;
  letter-spacing: 0.16px;
  color: var(--sc-gg-text-primary);
}

/* Enter/leave fade — the classes land on this component's root, so they belong
   here rather than in whichever parent wraps it in a <transition>. */
.gg-toast-fade-enter-active,
.gg-toast-fade-leave-active {
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard),
    transform var(--sc-duration-fast) var(--sc-easing-standard);
}

.gg-toast-fade-enter,
.gg-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(var(--sc-space-2));
}

.gg-toast__btn {
  flex-shrink: 0;
  min-width: 72px;
  min-height: 32px;
  padding: var(--sc-space-2) 6px;
  border-radius: var(--sc-radius-md);
  background: var(--sc-gg-neutral-light);
  color: var(--sc-gg-accent-strong);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    opacity: 0.88;
  }
}
</style>
